import React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  getScriptDisplayTitle,
  isUsableScriptText,
} from "@/features/opportunities/lib/script-display-title";
import {
  isShareBriefPayload,
  resolveShareFileHref,
  shareLinkQueryOptions,
  type ShareBriefPayload,
  type SharePayload,
  type ShareStoryboardImage,
} from "@/lib/queries";
import { OriginalBriefLink } from "@/features/opportunities/components/scripts/OriginalBriefLink";
import {
  resolveOriginalBriefUrl,
  shouldShowScriptRationale,
} from "@/features/opportunities/lib/original-brief-url";
import { formatPersonaBriefForCopy } from "@/features/persona-opportunities/lib/persona-brief-clipboard";
import { translatePersonaBriefClosedListValue } from "@/features/persona-opportunities/lib/persona-brief-options";
import { formatScriptHooksBlock } from "@/features/persona-opportunities/lib/persona-script-clipboard";
import type { TFunction } from "i18next";

export const Route = createFileRoute("/s/$token")({
  component: SharePage,
});

// Editorial serif stack — distinctive on Apple platforms, graceful Georgia fallback elsewhere.
const SERIF =
  "font-['Iowan_Old_Style','Palatino_Linotype','Palatino','Hoefler_Text','Georgia',serif]";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

// navigator.clipboard is undefined in non-secure contexts and writeText can
// reject, so guard and treat any failure as a no-op.
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (!navigator.clipboard?.writeText) return false;
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function buildCopyText(data: SharePayload, untitled: string, t: TFunction): string {
  const lines: string[] = [];
  const displayTitle = getScriptDisplayTitle(
    { hook: data.hook ?? null, concept: data.concept ?? null },
    untitled,
  );
  lines.push(displayTitle);
  lines.push("");
  if (data.adDescription) { lines.push(data.adDescription); lines.push(""); }
  if (data.sequence) lines.push(`Sequence: ${data.sequence}`);
  // Only list Concept as metadata when hook already provided the title line.
  if (isUsableScriptText(data.hook) && isUsableScriptText(data.concept)) {
    lines.push(`Concept: ${data.concept!.trim()}`);
  }
  if (data.duration) lines.push(`Duration: ${data.duration}`);
  if (data.presenter) lines.push(`Presenter: ${data.presenter}`);
  if (data.background) lines.push(`Background: ${data.background}`);
  if (data.product) lines.push(`Product: ${data.product}`);
  if (data.opportunityTitle) lines.push(`From: ${data.opportunityTitle}`);
  const hasSpecs =
    data.sequence ||
    (isUsableScriptText(data.hook) && isUsableScriptText(data.concept)) ||
    data.duration ||
    data.presenter ||
    data.background ||
    data.product ||
    data.opportunityTitle;
  if (hasSpecs) lines.push("");
  for (const sc of data.scenes) {
    lines.push(`Scene ${sc.scene_number} — ${sc.scene_name}`);
    lines.push(`  Seeing: ${sc.seeing}`);
    lines.push(`  Hearing: ${sc.hearing}`);
    if (sc.product) lines.push(`  Product: ${sc.product}`);
    if (sc.props) lines.push(`  Props: ${sc.props}`);
  }
  if (data.scenes.length > 0) lines.push("");
  if (data.script) lines.push(data.script);
  const hooksBlock = formatScriptHooksBlock(data.hooks ?? [], t);
  if (hooksBlock.length > 0) {
    if (lines.at(-1) !== "") lines.push("");
    lines.push(...hooksBlock);
  }
  const originalBriefUrl = resolveOriginalBriefUrl(data);
  if (shouldShowScriptRationale(data) && data.rationale) {
    lines.push("");
    lines.push("Why this works");
    lines.push(data.rationale);
  }
  if (originalBriefUrl) {
    lines.push("");
    lines.push(`View original brief: ${originalBriefUrl}`);
  }
  return lines.join("\n").trim();
}

// ─── Mobile scene field ─────────────────────────────────────────────────────
// A 5-column table is unreadable on phones, so on small screens each scene is
// rendered as a stacked block of labelled fields instead.

function SceneField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-stone-400">
        {label}
      </dt>
      <dd className="text-[15px] leading-relaxed text-stone-700">{value}</dd>
    </div>
  );
}

// Desktop table stacks Hearing / Product / Props in one column so Seeing, the
// longest field, keeps a readable measure inside the max-w-3xl page.
const SCENE_TABLE_COLUMNS = [
  { key: "num", labelKey: null, fallback: "", width: "w-8" },
  { key: "scene", labelKey: "sharePage.scenes.scene", fallback: "Scene", width: "w-28" },
  { key: "seeing", labelKey: "sharePage.scenes.seeing", fallback: "Seeing", width: "" },
  {
    key: "details",
    labelKey: "sharePage.scenes.details",
    fallback: "Hearing · Product · Props",
    width: "w-[38%]",
  },
] as const;

function StackedSceneField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-stone-400">
        {label}
      </dt>
      <dd className="leading-relaxed break-words text-stone-600">{value}</dd>
    </div>
  );
}

function BriefSpokenBlock({
  title,
  directionLabel,
  lineLabel,
  direction,
  line,
}: {
  title: string;
  directionLabel: string;
  lineLabel: string;
  direction: string;
  line: string;
}) {
  return (
    <section className="mt-10">
      <h2 className={`${SERIF} text-xl font-semibold text-stone-900`}>{title}</h2>
      <dl className="mt-3 space-y-3">
        <SceneField label={directionLabel} value={direction.trim()} />
        <SceneField label={lineLabel} value={line.trim()} />
      </dl>
    </section>
  );
}

function PublicBriefArticle({ brief }: { brief: ShareBriefPayload }) {
  const { t } = useTranslation();
  const title = brief.videoName.trim() || t("sharePage.brief.untitled", "Untitled brief");
  const directionLabel = t("personaOpportunities.scripts.briefs.copyDirection", "Direction");
  const lineLabel = t("personaOpportunities.scripts.briefs.copyLine", "Line");
  const notes = brief.notes.trim();
  const hooks = [...brief.hooks].sort((a, b) => a.hookIndex - b.hookIndex);

  return (
    <article className="motion-safe:animate-in motion-safe:fade-in motion-safe:duration-700">
      {brief.updatedAt ? (
        <p className="mb-5 text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-400">
          {fmtDate(brief.updatedAt)}
        </p>
      ) : null}
      <h1 className={`${SERIF} text-balance text-[1.9rem] font-semibold leading-[1.15] tracking-[-0.01em] text-stone-900 sm:text-[2.4rem]`}>
        {title}
      </h1>
      {brief.duration.trim() ? (
        <dl className="mt-8 border-y border-stone-200">
          <div className="grid grid-cols-[6.5rem_1fr] gap-5 py-3 sm:grid-cols-[8rem_1fr]">
            <dt className="pt-0.5 text-[11px] font-semibold uppercase tracking-wider text-stone-400">
              {t("personaOpportunities.scripts.briefs.duration", "Duration")}
            </dt>
            <dd className="text-[15px] leading-relaxed text-stone-700">{brief.duration.trim()}</dd>
          </div>
        </dl>
      ) : null}
      {hooks.length > 0 ? (
        <section className="mt-10">
          <h2 className={`${SERIF} border-b border-stone-300 pb-2.5 text-xl font-semibold text-stone-900`}>
            {t("personaOpportunities.scripts.briefs.hooksTitle", "Hooks")}
          </h2>
          <ol className="divide-y divide-stone-200">
            {hooks.map((hook) => {
              const isOneHook = brief.hookPackage === 1;
              const isSelected = isOneHook && hook.hookIndex === brief.selectedHookIndex;
              return (
              <li
                key={hook.hookIndex}
                className={
                  isSelected
                    ? "border-l-2 border-amber-600 bg-amber-50/80 py-5 pl-4"
                    : "py-5"
                }
              >
                <h3 className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1 font-medium text-stone-800">
                  <span>
                    {t("personaOpportunities.scripts.briefs.copyHook", {
                      defaultValue: "Hook {{index}}",
                      index: hook.hookIndex,
                    })}
                  </span>
                  {isSelected ? (
                    <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-700">
                      {t("sharePage.brief.selectedHook", "Selected")}
                    </span>
                  ) : null}
                </h3>
                <dl className="space-y-3">
                  <SceneField label={directionLabel} value={hook.direction.trim()} />
                  <SceneField label={lineLabel} value={hook.line.trim()} />
                </dl>
              </li>
              );
            })}
          </ol>
        </section>
      ) : null}
      <BriefSpokenBlock
        title={t("personaOpportunities.scripts.briefs.conceptTitle", "Concept")}
        directionLabel={directionLabel}
        lineLabel={lineLabel}
        direction={brief.concept.direction}
        line={brief.concept.line}
      />
      <BriefSpokenBlock
        title={t("personaOpportunities.scripts.briefs.ctaTitle", "Call to action")}
        directionLabel={directionLabel}
        lineLabel={lineLabel}
        direction={brief.cta.direction}
        line={brief.cta.line}
      />
      <section className="mt-10">
        <h2 className={`${SERIF} text-xl font-semibold text-stone-900`}>
          {t("personaOpportunities.scripts.briefs.profileTitle", "Creator profile")}
        </h2>
        <dl className="mt-3 divide-y divide-stone-200 border-y border-stone-200">
          <div className="grid grid-cols-[6.5rem_1fr] gap-5 py-3 sm:grid-cols-[8rem_1fr]">
            <dt className="pt-0.5 text-[11px] font-semibold uppercase tracking-wider text-stone-400">
              {t("personaOpportunities.scripts.briefs.ageRange", "Age range")}
            </dt>
            <dd className="text-[15px] leading-relaxed text-stone-700">
              {translatePersonaBriefClosedListValue(t, brief.ageRange)}
            </dd>
          </div>
          <div className="grid grid-cols-[6.5rem_1fr] gap-5 py-3 sm:grid-cols-[8rem_1fr]">
            <dt className="pt-0.5 text-[11px] font-semibold uppercase tracking-wider text-stone-400">
              {t("personaOpportunities.scripts.briefs.gender", "Gender")}
            </dt>
            <dd className="text-[15px] leading-relaxed text-stone-700">
              {translatePersonaBriefClosedListValue(t, brief.gender)}
            </dd>
          </div>
          {brief.preferences.trim() ? (
            <div className="grid grid-cols-[6.5rem_1fr] gap-5 py-3 sm:grid-cols-[8rem_1fr]">
              <dt className="pt-0.5 text-[11px] font-semibold uppercase tracking-wider text-stone-400">
                {t("personaOpportunities.scripts.briefs.preferences", "Preferences")}
              </dt>
              <dd className="whitespace-pre-wrap text-[15px] leading-relaxed text-stone-700">
                {brief.preferences.trim()}
              </dd>
            </div>
          ) : null}
        </dl>
      </section>
      {notes ? (
        <section className="mt-10">
          <h2 className={`${SERIF} text-xl font-semibold text-stone-900`}>
            {t("personaOpportunities.scripts.briefs.copyNotes", "Notes")}
          </h2>
          <p className="mt-3 whitespace-pre-wrap text-[15px] leading-relaxed text-stone-700">{notes}</p>
        </section>
      ) : null}
    </article>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function SharePage() {
  const { t } = useTranslation();
  const { token } = Route.useParams();
  const query = useQuery(shareLinkQueryOptions(token));
  const data = query.data;
  const brief = data && isShareBriefPayload(data) ? data : null;
  const script = data && !isShareBriefPayload(data) ? data : null;
  const untitled = t("sharePage.untitledTitle", "Untitled script");
  const displayTitle = brief
    ? brief.videoName.trim() || t("sharePage.brief.untitled", "Untitled brief")
    : script
      ? getScriptDisplayTitle(
          { hook: script.hook ?? null, concept: script.concept ?? null },
          untitled,
        )
      : null;

  const [scriptOpen, setScriptOpen] = React.useState(false);
  const [contentCopied, setContentCopied] = React.useState(false);
  const [linkCopied, setLinkCopied] = React.useState(false);

  // Referrer-Policy: no-referrer — token is a capability URL, don't leak it
  React.useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  // Dynamic document title
  React.useEffect(() => {
    document.title = displayTitle
      ? `${displayTitle} · Adge AI`
      : "Shared Script · Adge AI";
    return () => { document.title = "Adge AI"; };
  }, [displayTitle]);

  function copyScript() {
    if (!script) return;
    void copyToClipboard(buildCopyText(script, untitled, t)).then((ok) => {
      if (!ok) return;
      setContentCopied(true);
      setTimeout(() => setContentCopied(false), 2000);
    });
  }

  function copyBrief() {
    if (!brief) return;
    void copyToClipboard(formatPersonaBriefForCopy(brief, t)).then((ok) => {
      if (!ok) return;
      setContentCopied(true);
      setTimeout(() => setContentCopied(false), 2000);
    });
  }

  function copyLink() {
    void copyToClipboard(window.location.href).then((ok) => {
      if (!ok) return;
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  }

  // Signing can fail per-image, in which case the API sends imageUrl: null —
  // show nothing rather than a broken frame on a page sent to an outsider.
  const storyboardImages = (script?.storyboardImages ?? []).filter(
    (img): img is ShareStoryboardImage & { imageUrl: string } => !!img.imageUrl,
  );

  const metaRows = script
    ? [
        { label: "Concept", value: script.concept },
        { label: "Product", value: script.product },
        { label: "Duration", value: script.duration },
        { label: "Presenter", value: script.presenter },
        { label: "Background", value: script.background },
      ].filter((row): row is { label: string; value: string } =>
        isUsableScriptText(row.value),
      )
    : [];
  const originalBriefUrl = script ? resolveOriginalBriefUrl(script) : null;
  const showRationale = script ? shouldShowScriptRationale(script) : false;
  const hooks = [...(script?.hooks ?? [])].sort((a, b) => a.position - b.position);
  const updatedAt = brief?.updatedAt ?? script?.updatedAt ?? null;

  return (
    <div className="min-h-dvh flex flex-col bg-[#faf9f7] text-stone-900 antialiased selection:bg-amber-200/60">
      <header className="sticky top-0 z-10 border-b border-stone-200/70 bg-[#faf9f7]/80 backdrop-blur-sm">
        <div className="mx-auto flex h-14 w-full max-w-3xl items-center justify-between gap-4 px-5">
          <div className="flex min-w-0 items-center gap-2.5">
            <a
              href="https://adge.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 whitespace-nowrap rounded text-sm font-semibold tracking-tight text-stone-900 no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/40"
            >
              Adge AI
            </a>
            <span className="hidden h-3.5 w-px bg-stone-300 sm:inline-block" />
            <span className="hidden whitespace-nowrap text-[13px] text-stone-400 sm:inline">
              {brief
                ? t("sharePage.brief.eyebrow", "Shared brief")
                : "Shared script"}
            </span>
          </div>
          {(script || brief) && (
            <div className="flex shrink-0 items-center gap-2">
              <button
                onClick={copyLink}
                className="inline-flex min-h-11 items-center whitespace-nowrap rounded-md border border-stone-300 px-3 text-[13px] text-stone-600 transition-colors hover:bg-stone-100 hover:text-stone-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/40"
              >
                {linkCopied ? "Copied!" : "Copy link"}
              </button>
              {script && !script.fileDownload && (
                <button
                  onClick={copyScript}
                  className="inline-flex min-h-11 items-center whitespace-nowrap rounded-md bg-stone-900 px-3 text-[13px] font-medium text-stone-50 transition-colors hover:bg-stone-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#faf9f7]"
                >
                  {contentCopied ? "Copied!" : "Copy script"}
                </button>
              )}
              {brief && (
                <button
                  onClick={copyBrief}
                  className="inline-flex min-h-11 items-center whitespace-nowrap rounded-md bg-stone-900 px-3 text-[13px] font-medium text-stone-50 transition-colors hover:bg-stone-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#faf9f7]"
                >
                  {contentCopied
                    ? "Copied!"
                    : t("sharePage.brief.copyBrief", "Copy brief")}
                </button>
              )}
            </div>
          )}
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl flex-1 px-5 py-14 sm:py-20">
        {query.isLoading && (
          <div className="space-y-6" aria-busy="true">
            <div className="h-3 w-40 animate-pulse rounded bg-stone-200" />
            <div className="space-y-3">
              <div className="h-7 w-full animate-pulse rounded bg-stone-200" />
              <div className="h-7 w-2/3 animate-pulse rounded bg-stone-200" />
            </div>
            <div className="h-20 w-full animate-pulse rounded bg-stone-100" />
          </div>
        )}

        {query.isError && (
          <div className="flex flex-col items-center justify-center gap-4 py-28 text-center">
            <p className={`${SERIF} text-2xl text-stone-800`}>
              {t("sharePage.unavailable.title", "Couldn't open this link.")}
            </p>
            <p className="max-w-sm text-sm text-stone-500">
              {t(
                "sharePage.unavailable.help",
                "It may have been removed, or the connection failed. Try again, or ask the sender for a fresh link.",
              )}
            </p>
            <a
              href="https://adge.ai"
              className="mt-1 text-sm font-medium text-amber-700 underline-offset-4 hover:underline"
            >
              Go to Adge AI →
            </a>
          </div>
        )}

        {brief ? <PublicBriefArticle brief={brief} /> : null}

        {script && (
          <article className="motion-safe:animate-in motion-safe:fade-in motion-safe:duration-700">
            <section className="motion-safe:animate-in motion-safe:slide-in-from-bottom-3 motion-safe:fill-mode-both motion-safe:duration-700">
              {(script.opportunityTitle || script.updatedAt) && (
                <p className="mb-5 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-700">
                  {script.opportunityTitle}
                  {script.opportunityTitle && script.updatedAt && (
                    <span className="text-amber-700/40"> · </span>
                  )}
                  {script.updatedAt && (
                    <span className="text-stone-400">{fmtDate(script.updatedAt)}</span>
                  )}
                </p>
              )}

              {displayTitle && (
                <h1 className={`${SERIF} text-balance text-[1.9rem] font-semibold leading-[1.15] tracking-[-0.01em] text-stone-900 sm:text-[2.4rem]`}>
                  {displayTitle}
                </h1>
              )}

              {script.adDescription && (
                <p className="mt-6 max-w-prose text-[1.0625rem] leading-relaxed text-stone-600">
                  {script.adDescription}
                </p>
              )}
            </section>

            {storyboardImages.length > 0 && (
              <section className="mt-10 motion-safe:animate-in motion-safe:fade-in motion-safe:fill-mode-both motion-safe:delay-75 motion-safe:duration-700">
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-400">
                  {t("sharePage.storyboard.title", "Storyboard")}
                </p>
                <div className="space-y-4">
                  {storyboardImages.map((img, index) => (
                    <img
                      key={`${img.model}-${index}`}
                      src={img.imageUrl}
                      alt={t("sharePage.storyboard.imageAlt", "Storyboard for this script")}
                      loading="lazy"
                      className="aspect-video w-full rounded-md border border-stone-200 bg-stone-100 object-cover"
                    />
                  ))}
                </div>
              </section>
            )}

            {metaRows.length > 0 && (
              <dl className="mt-10 divide-y divide-stone-200 border-y border-stone-200 motion-safe:animate-in motion-safe:fade-in motion-safe:fill-mode-both motion-safe:delay-100 motion-safe:duration-700">
                {metaRows.map((row) => (
                  <div key={row.label} className="grid grid-cols-[6.5rem_1fr] gap-5 py-3 sm:grid-cols-[8rem_1fr]">
                    <dt className="pt-0.5 text-[11px] font-semibold uppercase tracking-wider text-stone-400">
                      {row.label}
                    </dt>
                    <dd className="text-[15px] leading-relaxed text-stone-700">
                      {row.value}
                      {row.label === "Concept" && originalBriefUrl ? (
                        <div className="mt-2">
                          <OriginalBriefLink
                            url={originalBriefUrl}
                            className="text-[15px] text-stone-700"
                          />
                        </div>
                      ) : null}
                    </dd>
                  </div>
                ))}
              </dl>
            )}

            {!isUsableScriptText(script.concept) && originalBriefUrl ? (
              <div className="mt-10 border-b border-stone-200 pb-3 motion-safe:animate-in motion-safe:fade-in motion-safe:fill-mode-both motion-safe:delay-100 motion-safe:duration-700">
                <OriginalBriefLink
                  url={originalBriefUrl}
                  className="text-[15px] text-stone-700"
                />
              </div>
            ) : null}

            {script.fileDownload ? (
              <section className="mt-10 motion-safe:animate-in motion-safe:fade-in motion-safe:fill-mode-both motion-safe:delay-100 motion-safe:duration-700">
                {script.fileDownload.url ? (
                  <a
                    href={resolveShareFileHref(script.fileDownload.url)}
                    download={script.fileDownload.fileName ?? ""}
                    className="inline-flex min-h-11 items-center rounded-md bg-stone-900 px-4 text-[13px] font-medium text-stone-50 no-underline transition-colors hover:bg-stone-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#faf9f7]"
                  >
                    {t("sharePage.fileDownload.action", "Download the file")}
                  </a>
                ) : (
                  <div className="max-w-prose">
                    <p className={`${SERIF} text-xl font-semibold text-stone-900`}>
                      {t("sharePage.fileDownload.empty", "Download the file")}
                    </p>
                    <p className="mt-2 text-sm leading-relaxed text-stone-500">
                      {t(
                        "sharePage.fileDownload.emptyHelp",
                        "This script was shared as an uploaded file. Ask the sender for a fresh link if the download is missing.",
                      )}
                    </p>
                  </div>
                )}
                {script.fileDownload.fileName ? (
                  <p className="mt-3 text-sm text-stone-500">
                    {t("sharePage.fileDownload.fileName", "File: {{name}}", {
                      name: script.fileDownload.fileName,
                    })}
                  </p>
                ) : null}
              </section>
            ) : null}

            {script.scenes.length > 0 && (
              <section className="mt-14 motion-safe:animate-in motion-safe:fade-in motion-safe:fill-mode-both motion-safe:delay-150 motion-safe:duration-700">
                <div className="flex items-baseline justify-between border-b border-stone-300 pb-2.5">
                  <h2 className={`${SERIF} text-xl font-semibold text-stone-900`}>Scene breakdown</h2>
                  <span className="text-xs tabular-nums text-stone-400">
                    {script.scenes.length} {script.scenes.length === 1 ? "scene" : "scenes"}
                  </span>
                </div>
                {/* Mobile (<sm): stacked, full-width scene blocks */}
                <ul className="divide-y divide-stone-200 sm:hidden">
                  {script.scenes.map((sc) => (
                    <li key={`m-${sc.scene_number}-${sc.scene_name}`} className="py-5">
                      <div className="mb-3 flex items-baseline gap-2.5">
                        <span className="font-mono text-xs tabular-nums text-stone-400">
                          {String(sc.scene_number).padStart(2, "0")}
                        </span>
                        <h3 className="font-medium text-stone-800">{sc.scene_name}</h3>
                      </div>
                      <dl className="space-y-3">
                        <SceneField label={t("sharePage.scenes.seeing", "Seeing")} value={sc.seeing} />
                        <SceneField label={t("sharePage.scenes.hearing", "Hearing")} value={sc.hearing} />
                        {sc.props && <SceneField label={t("sharePage.scenes.props", "Props")} value={sc.props} />}
                        {sc.product && <SceneField label={t("sharePage.scenes.product", "Product")} value={sc.product} />}
                      </dl>
                    </li>
                  ))}
                </ul>

                {/* Desktop (sm+): full scene table */}
                <div className="hidden sm:block">
                  <table className="w-full table-fixed border-collapse text-sm">
                    <thead>
                      <tr className="text-left align-bottom">
                        {SCENE_TABLE_COLUMNS.map((column) => (
                          <th
                            key={column.key}
                            className={`py-2.5 pr-4 text-[10px] font-semibold uppercase tracking-wider text-stone-400 ${column.width}`}
                          >
                            {column.labelKey ? t(column.labelKey, column.fallback) : column.fallback}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-200">
                      {script.scenes.map((sc) => (
                        <tr
                          key={`${sc.scene_number}-${sc.scene_name}`}
                          className="align-top transition-colors hover:bg-stone-100/60"
                        >
                          <td className="py-4 pr-4 font-mono text-xs text-stone-400 tabular-nums">
                            {String(sc.scene_number).padStart(2, "0")}
                          </td>
                          <td className="py-4 pr-4 font-medium break-words text-stone-800">{sc.scene_name}</td>
                          <td className="py-4 pr-4 leading-relaxed break-words text-stone-600">{sc.seeing}</td>
                          <td className="py-4">
                            <dl className="space-y-3">
                              <StackedSceneField label={t("sharePage.scenes.hearing", "Hearing")} value={sc.hearing} />
                              {sc.product && <StackedSceneField label={t("sharePage.scenes.product", "Product")} value={sc.product} />}
                              {sc.props && <StackedSceneField label={t("sharePage.scenes.props", "Props")} value={sc.props} />}
                            </dl>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {hooks.length > 1 && (
              <section
                aria-labelledby="share-hooks-title"
                className="mt-14 motion-safe:animate-in motion-safe:fade-in motion-safe:fill-mode-both motion-safe:delay-150 motion-safe:duration-700"
              >
                <div className="flex items-baseline justify-between border-b border-stone-300 pb-2.5">
                  <h2 id="share-hooks-title" className={`${SERIF} text-xl font-semibold text-stone-900`}>
                    {t("sharePage.hooks.title", "Scene 1 hooks")}
                  </h2>
                  <span className="text-xs tabular-nums text-stone-400">
                    {t("sharePage.hooks.count", {
                      defaultValue_one: "{{count}} hook",
                      defaultValue_other: "{{count}} hooks",
                      count: hooks.length,
                    })}
                  </span>
                </div>
                <ol className="divide-y divide-stone-200">
                  {hooks.map((hook, index) => (
                    <li key={hook.position} className="py-5">
                      <div className="mb-3 flex items-baseline gap-2.5">
                        <span className="font-mono text-xs tabular-nums text-stone-400">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <h3 className="font-medium text-stone-800">
                          {t("sharePage.hooks.hook", { defaultValue: "Hook {{index}}", index: index + 1 })}
                        </h3>
                        {index === 0 && (
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-700">
                            {t("sharePage.hooks.primary", "Primary")}
                          </span>
                        )}
                      </div>
                      <dl className="grid gap-3 sm:grid-cols-2 sm:gap-5">
                        <SceneField label={t("sharePage.hooks.voiceover", "Voiceover")} value={hook.voiceover} />
                        {hook.onScreenText && (
                          <SceneField
                            label={t("sharePage.hooks.onScreenText", "On-screen text")}
                            value={hook.onScreenText}
                          />
                        )}
                      </dl>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {isUsableScriptText(script.script) && (
              <section className="mt-12 border-t border-stone-200 pt-6">
                <button
                  onClick={() => setScriptOpen((o) => !o)}
                  className="group flex w-full items-center justify-between gap-4 text-left focus-visible:outline-none"
                  aria-expanded={scriptOpen}
                >
                  <span className={`${SERIF} text-xl font-semibold text-stone-900 group-focus-visible:underline group-focus-visible:underline-offset-4`}>
                    Full script
                  </span>
                  <svg
                    className={`h-4 w-4 shrink-0 text-stone-400 transition-transform duration-300 group-hover:text-stone-600 ${scriptOpen ? "rotate-180" : ""}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div
                  className={`grid transition-all duration-300 ease-in-out ${scriptOpen ? "mt-4 grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
                >
                  <div className="overflow-hidden">
                    <pre className="whitespace-pre-wrap font-sans text-[15px] leading-relaxed text-stone-700">
                      {script.script}
                    </pre>
                  </div>
                </div>
              </section>
            )}

            {showRationale && script.rationale && (
              <aside className="mt-14 border-l-2 border-amber-600/70 pl-6">
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-700">
                  Why this works
                </p>
                <p className={`${SERIF} text-lg leading-relaxed text-stone-700`}>
                  {script.rationale}
                </p>
              </aside>
            )}
          </article>
        )}
      </main>

      <footer className="border-t border-stone-200/70 px-5 py-7 text-center text-xs text-stone-400">
        Shared via{" "}
        <a href="https://adge.ai" className="text-stone-500 underline-offset-2 hover:underline">
          Adge AI
        </a>
        {updatedAt ? ` · last updated ${fmtDate(updatedAt)}` : ""}.{" "}
        {brief
          ? t("sharePage.brief.footerNote", "Content reflects the latest version of the brief.")
          : "Content reflects the latest version of the script."}
      </footer>
    </div>
  );
}
